import React, { useState, useEffect, useRef } from 'react';
import { storeGetOrDefault, storeSet, hydrateFromServer, DEF_OTHER_PRODUCTS } from '../../store';
import '../admin.css';
import './OtherProductsAdmin.css';

/* ── Toast ───────────────────────────────────────────────────────── */
const Toast = ({ msg, type }) =>
  msg ? <div className={`adm-toast adm-toast-${type}`}>{msg}</div> : null;

/* ── Compact image field ─────────────────────────────────────────── */
const ImgField = ({ value, onChange }) => {
  const ref = useRef();
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="opa-img-row">
      <div className="opa-img-left">
        {value
          ? <img src={value} alt="preview" className="opa-img-preview" />
          : <div className="opa-img-empty"><span>No image</span></div>
        }
      </div>
      <div className="opa-img-right">
        <label className="adm-label">Product Image</label>
        <input
          className="adm-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste image URL…"
        />
        <button
          type="button"
          className="opa-upload-btn"
          onClick={() => ref.current.click()}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Upload from device
        </button>
        <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
        {value && (
          <button type="button" className="opa-clear-btn" onClick={() => onChange('')}>
            ✕ Remove image
          </button>
        )}
      </div>
    </div>
  );
};

/* ── Colour field ────────────────────────────────────────────────── */
const ColourField = ({ label, value, onChange }) => (
  <div className="adm-form-group">
    <label className="adm-label">{label}</label>
    <div className="opa-colour-row">
      <input type="color" value={value || '#1e3a5f'} onChange={onChange}
        className="opa-colour-swatch" />
      <input className="adm-input opa-colour-text" value={value || ''} onChange={onChange}
        placeholder="#1e3a5f" />
    </div>
  </div>
);

/* ── Empty state ─────────────────────────────────────────────────── */
const EmptyState = ({ onAdd }) => (
  <div className="opa-empty">
    <div className="opa-empty-icon">📦</div>
    <h3>No products yet</h3>
    <p>Use the button below to add your first product.</p>
    <button className="opa-add-btn" onClick={onAdd}>+ Add First Product</button>
  </div>
);

/* ══════════════════════════════════════════════════════════════════
   Main component
   ══════════════════════════════════════════════════════════════════ */
const OtherProductsAdmin = () => {
  const [products, setProducts] = useState(() => storeGetOrDefault('otherProducts', DEF_OTHER_PRODUCTS));
  const [toast,    setToast]    = useState({ msg: '', type: 'success' });
  const [open,     setOpen]     = useState({});   // which cards are expanded

  /* Server hydration + cross-tab sync */
  useEffect(() => {
    hydrateFromServer().then(() => {
      setProducts(storeGetOrDefault('otherProducts', DEF_OTHER_PRODUCTS));
    });
    const onUpdate = (e) => {
      if (!e.detail?.key || e.detail.key === 'otherProducts') {
        setProducts(storeGetOrDefault('otherProducts', DEF_OTHER_PRODUCTS));
      }
    };
    window.addEventListener('zinga-store-update', onUpdate);
    return () => window.removeEventListener('zinga-store-update', onUpdate);
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3000);
  };

  const save = () => {
    storeSet('otherProducts', products);
    showToast('Saved and synced across all devices!');
  };

  /* Toggle a card open/closed */
  const toggle = (idx) => setOpen(o => ({ ...o, [idx]: !o[idx] }));

  /* ── Product-level helpers ────────────────────────────────────── */
  const setField   = (idx, k) => (val) =>
    setProducts(ps => ps.map((p, i) => i === idx ? { ...p, [k]: val } : p));
  const setFieldEv = (idx, k) => (e) => setField(idx, k)(e.target.value);

  const addProduct = () => {
    const newIdx = products.length;
    const newProd = {
      id: `product-${Date.now()}`,
      name: '', category: '', badge: '', badgeColor: '#2563eb',
      image: '', gradientFrom: '#1e3a5f', gradientTo: '#2563eb',
      desc: '', features: [], specs: [],
    };
    setProducts(ps => [...ps, newProd]);
    setOpen(o => ({ ...o, [newIdx]: true }));
    setTimeout(() => {
      document.getElementById(`prod-card-${newIdx}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const removeProduct = (idx) => {
    if (!window.confirm('Remove this product?')) return;
    setProducts(ps => ps.filter((_, i) => i !== idx));
    setOpen(o => {
      const n = { ...o };
      delete n[idx];
      return n;
    });
  };

  const moveProduct = (idx, dir) =>
    setProducts(ps => {
      const arr = [...ps];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });

  /* ── Feature helpers ─────────────────────────────────────────── */
  const setFeature    = (pIdx, fIdx) => (e) =>
    setProducts(ps => ps.map((p, i) => i === pIdx
      ? { ...p, features: p.features.map((f, j) => j === fIdx ? e.target.value : f) }
      : p));
  const addFeature    = (pIdx) =>
    setProducts(ps => ps.map((p, i) => i === pIdx ? { ...p, features: [...p.features, ''] } : p));
  const removeFeature = (pIdx, fIdx) =>
    setProducts(ps => ps.map((p, i) => i === pIdx
      ? { ...p, features: p.features.filter((_, j) => j !== fIdx) }
      : p));

  /* ── Spec helpers ─────────────────────────────────────────────── */
  const setSpec    = (pIdx, sIdx, k) => (e) =>
    setProducts(ps => ps.map((p, i) => i === pIdx
      ? { ...p, specs: p.specs.map((s, j) => j === sIdx ? { ...s, [k]: e.target.value } : s) }
      : p));
  const toggleBold = (pIdx, sIdx) =>
    setProducts(ps => ps.map((p, i) => i === pIdx
      ? { ...p, specs: p.specs.map((s, j) => j === sIdx ? { ...s, bold: !s.bold } : s) }
      : p));
  const addSpec    = (pIdx) =>
    setProducts(ps => ps.map((p, i) => i === pIdx
      ? { ...p, specs: [...p.specs, { label: '', value: '', bold: false }] }
      : p));
  const removeSpec = (pIdx, sIdx) =>
    setProducts(ps => ps.map((p, i) => i === pIdx
      ? { ...p, specs: p.specs.filter((_, j) => j !== sIdx) }
      : p));

  /* ══ render ═══════════════════════════════════════════════════ */
  return (
    <div className="adm-page opa-page">
      <Toast {...toast} />

      {/* ── Page header ─────────────────────────────────────── */}
      <div className="opa-header">
        <div className="opa-header-left">
          <div className="opa-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l9 4.5v9L12 21l-9-4.5v-9L12 3z"/><path d="M12 3v18"/><path d="M3 7.5l9 4.5 9-4.5"/>
            </svg>
          </div>
          <div>
            <h1 className="opa-header-title">Other Products</h1>
            <p className="opa-header-sub">{products.length} product{products.length !== 1 ? 's' : ''} · changes sync across all devices</p>
          </div>
        </div>
        <div className="opa-header-actions">
          <button className="opa-save-btn" onClick={save}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            Save &amp; Sync
          </button>
        </div>
      </div>

      {/* ── Add product button (always visible, never blocked) ── */}
      <button className="opa-add-btn-top" onClick={addProduct}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        Add Product
      </button>

      {/* ── Empty state ─────────────────────────────────────── */}
      {products.length === 0 && <EmptyState onAdd={addProduct} />}

      {/* ── Product cards ────────────────────────────────────── */}
      {products.map((p, idx) => (
        <div key={p.id || idx} id={`prod-card-${idx}`} className="opa-card">

          {/* Card header / toggle row */}
          <div className="opa-card-header" onClick={() => toggle(idx)}>
            <div className="opa-card-thumb">
              {p.image
                ? <img src={p.image} alt="" className="opa-thumb-img" />
                : <div className="opa-thumb-grad" style={{ background: `linear-gradient(135deg,${p.gradientFrom||'#1e3a5f'},${p.gradientTo||'#2563eb'})` }} />
              }
            </div>
            <div className="opa-card-info">
              <div className="opa-card-name">{p.name || <span className="opa-unnamed">Untitled product</span>}</div>
              <div className="opa-card-meta">
                {p.category && <span className="opa-tag">{p.category}</span>}
                {p.badge && <span className="opa-tag opa-badge-tag" style={{ background: p.badgeColor||'#2563eb' }}>{p.badge}</span>}
                <span className="opa-tag opa-neutral">{(p.features||[]).length} features · {(p.specs||[]).length} specs</span>
              </div>
            </div>
            <div className="opa-card-controls" onClick={e => e.stopPropagation()}>
              <button className="opa-ctrl-btn" onClick={() => moveProduct(idx, -1)} disabled={idx === 0} title="Move up">▲</button>
              <button className="opa-ctrl-btn" onClick={() => moveProduct(idx,  1)} disabled={idx === products.length - 1} title="Move down">▼</button>
              <button className="opa-ctrl-btn opa-ctrl-delete" onClick={() => removeProduct(idx)} title="Remove">✕</button>
            </div>
            <div className="opa-card-toggle">{open[idx] ? '▲' : '▼'}</div>
          </div>

          {/* Expanded form */}
          {open[idx] && (
            <div className="opa-card-body">

              {/* ── Section: Basic Info ─────────────────────── */}
              <div className="opa-section">
                <div className="opa-section-label">Basic Information</div>
                <div className="adm-form-row">
                  <div className="adm-form-group">
                    <label className="adm-label">Product Name</label>
                    <input className="adm-input" value={p.name} onChange={setFieldEv(idx,'name')} placeholder="e.g. Epoxy Coating System" />
                  </div>
                  <div className="adm-form-group">
                    <label className="adm-label">Category</label>
                    <input className="adm-input" value={p.category} onChange={setFieldEv(idx,'category')} placeholder="e.g. Industrial Coatings" />
                  </div>
                </div>
                <div className="adm-form-group">
                  <label className="adm-label">Description</label>
                  <textarea className="adm-input adm-textarea" rows={3} value={p.desc} onChange={setFieldEv(idx,'desc')} placeholder="Full product description…" />
                </div>
              </div>

              {/* ── Section: Badge ──────────────────────────── */}
              <div className="opa-section">
                <div className="opa-section-label">Badge <span className="opa-optional">(optional)</span></div>
                <div className="adm-form-row">
                  <div className="adm-form-group">
                    <label className="adm-label">Badge Text</label>
                    <input className="adm-input" value={p.badge} onChange={setFieldEv(idx,'badge')} placeholder="e.g. Bestseller" />
                  </div>
                  <ColourField label="Badge Colour" value={p.badgeColor} onChange={setFieldEv(idx,'badgeColor')} />
                </div>
              </div>

              {/* ── Section: Image ──────────────────────────── */}
              <div className="opa-section">
                <div className="opa-section-label">Product Image</div>
                <ImgField value={p.image||''} onChange={setField(idx,'image')} />
              </div>

              {/* ── Section: Gradient ───────────────────────── */}
              <div className="opa-section">
                <div className="opa-section-label">
                  Card Background Gradient
                  <span className="opa-optional"> — used when no image is set</span>
                </div>
                <div className="adm-form-row">
                  <ColourField label="From colour" value={p.gradientFrom} onChange={setFieldEv(idx,'gradientFrom')} />
                  <ColourField label="To colour"   value={p.gradientTo}   onChange={setFieldEv(idx,'gradientTo')}   />
                </div>
                <div className="opa-grad-preview" style={{ background: `linear-gradient(135deg,${p.gradientFrom||'#1e3a5f'},${p.gradientTo||'#2563eb'})` }}>
                  Preview
                </div>
              </div>

              {/* ── Section: Features ───────────────────────── */}
              <div className="opa-section">
                <div className="opa-section-header">
                  <div className="opa-section-label">Feature Bullet Points</div>
                  <button className="opa-mini-add" onClick={() => addFeature(idx)}>+ Add feature</button>
                </div>
                {(p.features||[]).length === 0
                  ? <p className="opa-hint">No features yet — click "+ Add feature" above.</p>
                  : (p.features||[]).map((f, fIdx) => (
                    <div key={fIdx} className="opa-list-row">
                      <span className="opa-list-num">{fIdx+1}</span>
                      <input className="adm-input" value={f} onChange={setFeature(idx,fIdx)} placeholder="e.g. UV resistant, chemical proof…" />
                      <button className="opa-list-del" onClick={() => removeFeature(idx,fIdx)}>✕</button>
                    </div>
                  ))
                }
              </div>

              {/* ── Section: Specs ──────────────────────────── */}
              <div className="opa-section">
                <div className="opa-section-header">
                  <div className="opa-section-label">Specifications Table</div>
                  <button className="opa-mini-add" onClick={() => addSpec(idx)}>+ Add row</button>
                </div>
                {(p.specs||[]).length === 0
                  ? <p className="opa-hint">No specs yet — click "+ Add row" above.</p>
                  : <>
                    <div className="opa-spec-head">
                      <span>Label</span><span>Value</span><span>Bold</span><span></span>
                    </div>
                    {(p.specs||[]).map((sp, sIdx) => (
                      <div key={sIdx} className="opa-spec-row">
                        <input className="adm-input" value={sp.label} onChange={setSpec(idx,sIdx,'label')} placeholder="e.g. Coverage" />
                        <input className="adm-input" value={sp.value} onChange={setSpec(idx,sIdx,'value')} placeholder="e.g. 8–10 m²/L" />
                        <label className="opa-bold-label">
                          <input type="checkbox" checked={!!sp.bold} onChange={() => toggleBold(idx,sIdx)} /> Bold
                        </label>
                        <button className="opa-list-del" onClick={() => removeSpec(idx,sIdx)}>✕</button>
                      </div>
                    ))}
                  </>
                }
              </div>

              {/* ── Card-level save shortcut ─────────────────── */}
              <div className="opa-card-foot">
                <button className="opa-ctrl-btn opa-ctrl-delete" onClick={() => removeProduct(idx)}>✕ Remove Product</button>
                <button className="opa-save-btn" onClick={save}>💾 Save &amp; Sync</button>
              </div>

            </div>
          )}
        </div>
      ))}

      {/* ── Bottom add + save ──────────────────────────────── */}
      {products.length > 0 && (
        <div className="opa-bottom-bar">
          <button className="opa-add-btn-top" onClick={addProduct}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Another Product
          </button>
          <button className="opa-save-btn" onClick={save}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
            Save &amp; Sync All
          </button>
        </div>
      )}
    </div>
  );
};

export default OtherProductsAdmin;
